import { z } from "zod";
import type { Block } from "./doc";

/**
 * A print job's document as the server stores it (`print_jobs.document`):
 * the template's blocks, independent of paper width and print mode, so any
 * printer can print it. Never a bitmap: a logo is a `logo` block naming its
 * file, dithered by the printing device and cached per paper width (P5-T27).
 */

export const DOCUMENT_KINDS = ["bill", "kot", "kotCancel", "test", "dayClose"] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export type StoredBlock = Exclude<Block, { t: "bitmap" } | { t: "byWidth" }> | StoredByWidth;
type StoredByWidth = { t: "byWidth"; w58: StoredBlock[]; w80: StoredBlock[] };

const align = z.enum(["left", "center", "right"]);
const multiplier = z.number().int().min(1).max(8);
const styleSchema = z.object({
	align: align.optional(),
	bold: z.boolean().optional(),
	underline: z.boolean().optional(),
	invert: z.boolean().optional(),
	w: multiplier.optional(),
	h: multiplier.optional(),
	font: z.enum(["A", "B"]).optional(),
});
const text = z.string().max(2000);

const flatBlockSchema = z.discriminatedUnion("t", [
	z.object({ t: z.literal("text"), text, style: styleSchema.optional() }),
	z.object({
		t: z.literal("row"),
		cols: z
			.array(z.object({ text, weight: z.number().positive(), align: align.optional() }))
			.min(1)
			.max(8),
		style: styleSchema.optional(),
	}),
	z.object({ t: z.literal("rule"), ch: z.string().length(1).optional() }),
	z.object({ t: z.literal("feed"), lines: z.number().int().min(0).max(255) }),
	z.object({
		t: z.literal("qr"),
		data: z.string().min(1).max(700),
		size: z.number().int().min(1).max(16).optional(),
		align: align.optional(),
	}),
	z.object({
		t: z.literal("barcode"),
		data: z.string().min(1).max(60),
		height: z.number().int().min(1).max(255).optional(),
		align: align.optional(),
	}),
	z.object({ t: z.literal("cut"), full: z.boolean().optional() }),
	z.object({ t: z.literal("beep"), times: z.number().int().min(1).max(9).optional() }),
	z.object({ t: z.literal("drawer") }),
	z.object({ t: z.literal("logo"), fileId: z.uuid(), align: align.optional() }),
]);

export const storedBlockSchema: z.ZodType<StoredBlock> = z.union([
	flatBlockSchema,
	z.object({
		t: z.literal("byWidth"),
		get w58() {
			return z.array(storedBlockSchema);
		},
		get w80() {
			return z.array(storedBlockSchema);
		},
	}),
]);

export const printDocumentSchema = z.object({
	version: z.literal(1),
	kind: z.enum(DOCUMENT_KINDS),
	/** Printed above everything, such as "DUPLICATE" on a reprint. Already in `blocks`. */
	copyLabel: z.string().max(40).nullable(),
	blocks: z.array(storedBlockSchema).max(1000),
});
export type PrintDocument = z.infer<typeof printDocumentSchema>;
